import { useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { BaseMap, GeoJSONLayer } from '@/components/maps/LeafletMap';
import {
  MapLegend,
  MapPropertySelector,
} from '@/components/maps/insets';
import { MapLoaderOverlay } from '@/components/ui/loader';
import type { useSIPMapInteraction } from '@/hooks/sip/useSIPMapInteraction';
import type { useSIPFilters } from '@/hooks/sip/useSIPFilters';

type MapInteraction = ReturnType<typeof useSIPMapInteraction>;
type Filters = ReturnType<typeof useSIPFilters>;

interface MapTileProps {
  mapInteraction: MapInteraction;
  filters: Pick<
    Filters,
    | 'selectedVariable'
    | 'selectedModel'
    | 'selectedGeoNode'
    | 'isForecastDataLoading'
    | 'isFiltersLoading'
    | 'isGeoDataLoading'
    | 'availableRuns'
    | 'availableRunsLoading'
    | 'selectedWeatherModel'
    | 'isNoForecastFound'
    | 'handlePropertySelect'
  >;
  effectiveVariableUnits: string;
  leafHintVisible?: boolean;
}

export function MapTile({ mapInteraction, filters, effectiveVariableUnits, leafHintVisible }: MapTileProps) {
  const propertySelectorRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative h-full w-full">
      <MapLoaderOverlay
        loading={filters.isFiltersLoading || filters.isGeoDataLoading}
        text={filters.isFiltersLoading ? 'Loading filters...' : 'Loading map data...'}
      />

      {!filters.isFiltersLoading && !filters.isGeoDataLoading && filters.isForecastDataLoading && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-background/90 backdrop-blur-sm border border-border rounded-full px-4 py-1.5 shadow-sm flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-xs text-muted-foreground font-medium">Loading forecast data...</span>
        </div>
      )}

      {!filters.isFiltersLoading && !filters.isGeoDataLoading && !filters.isForecastDataLoading && filters.isNoForecastFound && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-destructive/10 text-destructive border border-destructive/30 rounded-full px-4 py-1.5 shadow-sm">
          <span className="text-xs font-medium">No forecast data available</span>
        </div>
      )}

      {!filters.isFiltersLoading && !filters.isGeoDataLoading && !filters.availableRunsLoading && filters.availableRuns.length === 0 && filters.selectedModel && filters.selectedWeatherModel && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/75 backdrop-blur-sm rounded-md">
          <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card px-8 py-6 shadow-lg max-w-md text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground" aria-hidden />
            <p className="text-base font-medium text-foreground">No runs available</p>
            <p className="text-sm text-muted-foreground">No model runs are available for the selected filters. Try choosing a different date, model, or weather model.</p>
          </div>
        </div>
      )}

      {leafHintVisible && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-background/90 backdrop-blur-sm border border-border rounded-full px-4 py-1.5 shadow-sm">
          <span className="text-xs text-muted-foreground font-medium">
            No further drill-down
          </span>
        </div>
      )}

      <BaseMap
        center={[39.8283, -98.5795]}
        zoom={5}
        className="h-full w-full border shadow-inner"
        style={{ height: '100%' }}
        showZoomControls={true}
        doubleClickZoom={false}
        closePopupOnClick={false}
      >
        {mapInteraction.mapGeoJSON && (
          <GeoJSONLayer
            dataKey={`${mapInteraction.geoDataKey}-${mapInteraction.forecastDataKey}-var-${mapInteraction.effectiveVariableId}`}
            fitBoundsKey={`model-${filters.selectedModel?.model_id}-geo-${filters.selectedGeoNode?.id}`}
            data={mapInteraction.mapGeoJSON}
            style={mapInteraction.getFeatureStyle}
            onEachFeature={mapInteraction.onEachFeature}
            fitBounds={true}
          />
        )}
      </BaseMap>

      <MapPropertySelector
        ref={propertySelectorRef}
        properties={mapInteraction.propertyOptions}
        defaultPropertyId={filters.selectedVariable?.variable_id ?? ''}
        onPropertyChange={filters.handlePropertySelect}
        position="top-right"
        loading={mapInteraction.isModelTransition}
      />

      <MapLegend
        propertyName={mapInteraction.effectivePropertyName}
        units={effectiveVariableUnits}
        bins={mapInteraction.colorBins}
        position="bottom-right"
        collapsed={mapInteraction.legendCollapsed}
        onToggle={() => mapInteraction.setLegendCollapsed(!mapInteraction.legendCollapsed)}
        loading={mapInteraction.isModelTransition}
        overlapRef={propertySelectorRef}
      />
    </div>
  );
}
